import { Fragment, h } from 'preact';
import {
	useContext, useState, useRef, useEffect,
} from 'preact/compat';
import { Theme } from '../../lib/render';
import { DonationStepEnum, useDonationStep } from '../../hooks/step';
import { scrollToSelector } from '../../lib/helpers';
import { useUser } from '../../hooks/user';
import purifyHTML from '../../lib/purify';
import Accordion from '../Accordion';
import Personal from './Personal';
import Payment from './Payment';
import Confirmation from './Confirmation';
import { useVGS } from '../../hooks/vgs';
import { useSubmit, SubmitProvider } from '../../hooks/submit';
import { validConfirmTransition } from '../../hooks/transitionGaurds';
import UpsellDef from './Upsell';
import SourceCode from '../inputs/SourceCode';

/**
 * Simple form section.
 * We visually hide form sections that are not active.
 * Sections must remain in DOM to prevent VGS re-rendering issues.
 */
const FormSection = ({
	active,
	children,
	hardConditional = true,
}: {
	active: boolean;
	children: h.JSX.Element;
	hardConditional?: boolean;
}) => {
	if (hardConditional) {
		return (
			<div
				className={[
					'ea-section',
					active ? 'active' : 'inactive',
				].join(' ')}
				aria-hidden={!active}
			>
				{children}
			</div>
		);
	}
	return null;
};

/**
 * Form layout for the donation form.
 * Handles progressing through the checkout process.
 * Process takes place within a single form element.
 */
const Form = ({
	Upsell,
	displaySrcCode,
}:{
	Upsell: h.JSX.Element;
	displaySrcCode: boolean;
}) => {
	const theme = useContext(Theme);
	const step = useDonationStep();
	const { addressValid, personalValid } = useUser();
	const { vgsValid } = useVGS();
	const active = step.state !== DonationStepEnum.ENTRY && step.state !== DonationStepEnum.SUCCESS;
	const { submit, errorMessage, submitAttempted } = useSubmit();

	/**
	 * Handle auto opening payment accordion,
	 * if the user has filled out personal and this is first pass.
	 */
	const [paymentOpen, setPaymentOpen] = useState(false);
	const firstTouch = useRef(true);
	useEffect(() => {
		if (firstTouch.current && !submitAttempted && personalValid) {
			setPaymentOpen(true);
			firstTouch.current = false;
		}
	}, [submitAttempted, firstTouch.current, personalValid]);

	/**
	 * Scroll to top on step change.
	 */
	useEffect(() => {
		if (step.state === DonationStepEnum.CHECKOUT) {
			scrollToSelector('.ea-form', -50, 100);
		}
	}, [step.state]);

	return (
		<form
			action=""
			className={[
				'ea-form',
				active ? 'active' : 'inactive',
			].join(' ')}
			onSubmit={(e) => {
				e.preventDefault();
				submit();
			}}
		>
			{/* Global Errors for flow */}
			{(submitAttempted && errorMessage) && (
				<div className="ea-form__top-error">
					<p>{errorMessage}</p>
				</div>
			)}
			{/* Payment + Personal */}
			<FormSection
				active={step.state === DonationStepEnum.CHECKOUT}
			>
				<Fragment>
					{displaySrcCode && (
						<SourceCode />
					)}
					<Accordion
						title="Personal"
						valid={personalValid}
						active
					>
						<Personal
							config={theme}
						/>
					</Accordion>
					<Accordion
						title="Payment"
						valid={vgsValid && addressValid}
						active={paymentOpen}
					>
						<Payment />
					</Accordion>
					<button
						className="ea-form__continue ea-btn ea-btn--invert ea-btn--lg"
						type="button"
						/**
						 * Only allow continue if payment and personal are valid.
						 */
						disabled={!validConfirmTransition()}
						onClick={() => {
							step.dispatch({
								type: 'MANUAL_STEP',
								payload: {
									step: DonationStepEnum.CONFIRM,
								},
							});
						}}
					>
						<span>Confirm Payment Details</span>
					</button>
					{theme.terms && (
						<div
							className="ea-terms-of-service ea-xsmall"
							dangerouslySetInnerHTML={{ __html: purifyHTML(theme.terms) }}
						/>
					)}
				</Fragment>
			</FormSection>
			{/* Confirmation Subflow (contains optional upsell) */}
			<FormSection
				active={step.state === DonationStepEnum.CONFIRM}
				hardConditional={step.state === DonationStepEnum.CONFIRM}
			>
				<Confirmation
					Upsell={Upsell}
				/>
			</FormSection>
		</form>
	);
};

/**
 * Wrap form in a submit provider.
 * Define default upsell component.
 *
 * @param props.Upsell - Upsell component.
 * @param props.displaySrcCode - Display source code input capture (caging forms).
 */
const CheckoutForm = ({
	Upsell = <UpsellDef />,
	displaySrcCode = false,
}: {
	Upsell?: h.JSX.Element;
	displaySrcCode?: boolean;
}) => (
	<SubmitProvider>
		<Form
			Upsell={Upsell}
			displaySrcCode={displaySrcCode}
		/>
	</SubmitProvider>
);

export default CheckoutForm;
